import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc, arrayUnion, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { notifyOrderCreated } from '../services/NotificationSystem';
import { getOrCreateChatRoom } from '../utils/chatUtils';
import { getFeatures, isClinicScanIntakeOn } from '../utils/features';
import {
    PenLine, Highlighter, Eraser, Undo2, Redo2, Trash2, Send, Maximize,
    Minimize, ChevronLeft, Handshake, Loader, ScanLine,
} from 'lucide-react';

/**
 * 필기주문 — 태블릿(아이패드/갤럭시탭) 펜 필기로 의뢰서 작성
 * - 저지연: desynchronized 캔버스 + getCoalescedEvents + 베지어 스무딩 + 압력 반영
 * - 레이어 분리: 배경(양식)과 잉크 캔버스를 분리해 필기 중 배경 재그리기 없음
 * - 고정 모드: 전체화면 + Wake Lock + 스크롤/줌 차단 (필기 전용 태블릿 운영)
 * - 양식: 기공소 커스텀(handwrittenTemplateUrl) 우선, 없으면 덴클 표준 양식
 */

// 논리 캔버스 크기 (A4 150dpi — 인쇄 품질 확보)
const CANVAS_W = 1240;
const CANVAS_H = 1754;

const COLORS = ['#111827', '#1d4ed8', '#dc2626', '#059669'];
const SIZES = [2, 4, 7, 12];

// ── 덴클 표준 의뢰서 양식 (캔버스에 직접 그림) ──
function drawStandardTemplate(ctx) {
    const W = CANVAS_W, H = CANVAS_H, M = 60;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#94a3b8';
    ctx.fillStyle = '#334155';
    ctx.lineWidth = 2;

    // 제목
    ctx.font = 'bold 44px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('치과기공물 제작 의뢰서', W / 2, 110);
    ctx.font = '20px sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('DENTAL CLUSTER', W / 2, 145);

    const box = (x, y, w, h, label) => {
        ctx.strokeRect(x, y, w, h);
        if (label) {
            ctx.fillStyle = '#64748b';
            ctx.font = 'bold 22px sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(label, x + 14, y + 34);
        }
    };

    // 1행: 환자명 / 성별·나이 / 의뢰일 / 납기일
    const rowY = 190, rowH = 80;
    const cw = (W - M * 2) / 4;
    box(M, rowY, cw, rowH, '환자명');
    box(M + cw, rowY, cw, rowH, '성별 / 나이');
    box(M + cw * 2, rowY, cw, rowH, '의뢰일');
    box(M + cw * 3, rowY, cw, rowH, '납기일');

    // 치식표
    const chartY = 300, chartH = 420;
    box(M, chartY, W - M * 2, chartH, '치식 (해당 치아에 표시)');
    const cx = W / 2, cy = chartY + 70 + (chartH - 70) / 2;
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(M + 40, cy); ctx.lineTo(W - M - 40, cy);           // 가로선 (상/하악)
    ctx.moveTo(cx, chartY + 80); ctx.lineTo(cx, chartY + chartH - 20); // 세로선 (좌/우)
    ctx.stroke();
    // FDI 치아번호
    ctx.font = '26px sans-serif';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'center';
    const half = (cx - M - 60) / 8;
    for (let i = 0; i < 8; i++) {
        ctx.fillText(String(18 - i), M + 55 + half * i + half / 2, cy - 90);   // 18~11
        ctx.fillText(String(21 + i), cx + 20 + half * i + half / 2, cy - 90);  // 21~28
        ctx.fillText(String(48 - i), M + 55 + half * i + half / 2, cy + 110);  // 48~41
        ctx.fillText(String(31 + i), cx + 20 + half * i + half / 2, cy + 110); // 31~38
    }
    ctx.strokeStyle = '#94a3b8';

    // 보철/재료/쉐이드
    const fY = chartY + chartH + 20, fH = 90;
    const fw = (W - M * 2) / 3;
    box(M, fY, fw, fH, '보철물 종류');
    box(M + fw, fY, fw, fH, '재료');
    box(M + fw * 2, fY, fw, fH, 'Shade');

    // 임플란트
    box(M, fY + fH + 15, W - M * 2, 80, '임플란트 (제조사 / 종류)');

    // 요청사항
    const nY = fY + fH + 15 + 80 + 15;
    box(M, nY, W - M * 2, H - nY - 170, '요청사항');

    // 하단 법정 문구
    ctx.fillStyle = '#94a3b8';
    ctx.font = '19px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('의료기사 등에 관한 법률 제11조의3 및 같은 법 시행규칙 제12조의4 제1항에 따라 위와 같이 제작을 의뢰합니다.', W / 2, H - 110);
    ctx.font = 'bold 24px sans-serif';
    ctx.fillStyle = '#475569';
    ctx.fillText('의뢰 치과: ____________________     담당자: ____________  (서명)', W / 2, H - 60);
}

function HandwrittenOrder({ user }) {
    const navigate = useNavigate();
    const location = useLocation();

    // 단계: partner(거래처 선택) → draw(필기)
    const [step, setStep] = useState('partner');
    const [partners, setPartners] = useState([]);
    const [partner, setPartner] = useState(null); // { id, name, templateUrl }
    const [submitting, setSubmitting] = useState(false);

    // 통합 에이전트 스캔 연동 (?scanId=)
    const scanIdParam = new URLSearchParams(location.search).get('scanId');
    const [linkedScan, setLinkedScan] = useState(null);

    // 🦷 치과 스캔 수집 숨김 (2026-08-08) — 재개 = config/features.clinicScanIntake=true
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, []);
    // 판정: 플래그 && (베타 || stage 2 이상) — 2026-08-20 대표 확정 (베타 치과는 현행 유지)
    const scanIntakeOn = isClinicScanIntakeOn(features, user, user?.email);

    // 수정 모드 (?editOrderId=) — 기존 필기 이미지를 배경으로 깔고 덧쓰기
    const editOrderId = new URLSearchParams(location.search).get('editOrderId');
    const [editOrder, setEditOrder] = useState(null);

    useEffect(() => {
        if (!editOrderId) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'workOrders', editOrderId));
                if (!snap.exists() || !snap.data().handwrittenImageUrl) {
                    alert('필기 주문을 찾을 수 없습니다.');
                    navigate('/orders');
                    return;
                }
                const data = snap.data();
                setEditOrder({ id: snap.id, ...data });
                // 수정도 새로 작성 — 기공소 커스텀 양식이 있으면 그 양식으로
                const labSnap = await getDoc(doc(db, 'users', data.toUserId));
                setPartner({
                    id: data.toUserId,
                    name: data.toUserName,
                    templateUrl: labSnap.exists() ? (labSnap.data().handwrittenTemplateUrl || null) : null,
                });
                setStep('draw');
            } catch (e) {
                console.error('주문 로드 실패:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editOrderId]);

    useEffect(() => {
        if (!scanIdParam || !scanIntakeOn) return;   // 스캔 수집 숨김 시 ?scanId= 직접 접근도 무시
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'scans', scanIdParam));
                if (!snap.exists()) { alert('스캔 정보를 찾을 수 없습니다.'); return; }
                const data = { id: snap.id, ...snap.data() };
                if (data.orderId) { alert('이미 의뢰서가 작성된 스캔입니다.'); return; }
                setLinkedScan(data);
            } catch (e) { console.error('스캔 로드 실패:', e); }
        })();
    }, [scanIdParam, scanIntakeOn]);

    // 필기 도구 상태
    const [tool, setTool] = useState('pen');       // pen | highlighter | eraser
    const [color, setColor] = useState(COLORS[0]);
    const [size, setSize] = useState(4);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [pinned, setPinned] = useState(false);   // 화면 고정 모드
    const [allowTouch, setAllowTouch] = useState(false); // 손가락 필기 허용

    const bgRef = useRef(null);     // 양식 캔버스
    const inkRef = useRef(null);    // 잉크 캔버스
    const wrapRef = useRef(null);
    const pageRef = useRef(null);
    const strokesRef = useRef([]);  // [{ tool, color, size, points: [{x,y,p}] }]
    const redoRef = useRef([]);
    const currentRef = useRef(null);
    const wakeLockRef = useRef(null);
    const toolRef = useRef({ tool, color, size, allowTouch });
    toolRef.current = { tool, color, size, allowTouch };

    const targetUserId = user?.companyId || user?.uid;

    // ── 거래처 로드 ──
    useEffect(() => {
        if (!targetUserId) return;
        (async () => {
            try {
                // CreateOrder와 동일한 방식: connections는 requesterId/receiverId 구조
                const [sentSnap, receivedSnap] = await Promise.all([
                    getDocs(query(collection(db, 'connections'), where('requesterId', '==', targetUserId), where('status', '==', 'accepted'))),
                    getDocs(query(collection(db, 'connections'), where('receiverId', '==', targetUserId), where('status', '==', 'accepted'))),
                ]);
                const otherIds = new Set();
                sentSnap.docs.forEach(d => otherIds.add(d.data().receiverId));
                receivedSnap.docs.forEach(d => otherIds.add(d.data().requesterId));
                otherIds.delete(targetUserId);

                const list = [];
                for (const otherId of otherIds) {
                    const userSnap = await getDoc(doc(db, 'users', otherId));
                    if (userSnap.exists()) {
                        const u = userSnap.data();
                        list.push({
                            id: otherId,
                            name: u.businessName || u.name || '이름 없음',
                            templateUrl: u.handwrittenTemplateUrl || null,
                        });
                    }
                }
                setPartners(list);
            } catch (e) { console.error('거래처 로드 실패:', e); }
        })();
    }, [targetUserId]);

    // ── 캔버스 초기화 (필기 단계 진입 시) ──
    useEffect(() => {
        if (step !== 'draw') return;
        const bg = bgRef.current, ink = inkRef.current;
        bg.width = CANVAS_W; bg.height = CANVAS_H;
        ink.width = CANVAS_W; ink.height = CANVAS_H;
        const bgCtx = bg.getContext('2d');

        // 수정 모드도 빈 양식에서 새로 작성 (이전 필기는 이력으로 보관)
        const bgUrl = partner?.templateUrl;
        if (bgUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                bgCtx.fillStyle = '#fff';
                bgCtx.fillRect(0, 0, CANVAS_W, CANVAS_H);
                // 비율 유지하여 맞춤
                const s = Math.min(CANVAS_W / img.width, CANVAS_H / img.height);
                const w = img.width * s, h = img.height * s;
                bgCtx.drawImage(img, (CANVAS_W - w) / 2, (CANVAS_H - h) / 2, w, h);
            };
            img.onerror = () => drawStandardTemplate(bgCtx);
            img.src = bgUrl;
        } else {
            drawStandardTemplate(bgCtx);
        }

        strokesRef.current = [];
        redoRef.current = [];
        setCanUndo(false); setCanRedo(false);
    }, [step, partner, editOrder]);

    // ── 필기 엔진 ──
    const getInkCtx = () => inkRef.current.getContext('2d', { desynchronized: true });

    const toCanvasPoint = useCallback((e) => {
        const rect = inkRef.current.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (CANVAS_W / rect.width),
            y: (e.clientY - rect.top) * (CANVAS_H / rect.height),
            p: e.pressure && e.pressure > 0 ? e.pressure : 0.5,
        };
    }, []);

    const strokeStyle = (ctx, s) => {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (s.tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.globalAlpha = 1;
            ctx.strokeStyle = '#000';
        } else if (s.tool === 'highlighter') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = 0.35;
            ctx.strokeStyle = s.color;
        } else {
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = 1;
            ctx.strokeStyle = s.color;
        }
    };

    const widthFor = (s, p) => {
        const base = s.tool === 'highlighter' ? s.size * 4 : (s.tool === 'eraser' ? s.size * 6 : s.size);
        return s.tool === 'pen' ? base * (0.4 + p * 1.2) : base;
    };

    // 중점 베지어 스무딩으로 세그먼트 그리기 (마지막 3점)
    const drawSegment = useCallback((ctx, s, pts) => {
        const n = pts.length;
        if (n < 3) {
            if (n === 2) {
                strokeStyle(ctx, s);
                ctx.lineWidth = widthFor(s, pts[1].p);
                ctx.beginPath();
                ctx.moveTo(pts[0].x, pts[0].y);
                ctx.lineTo(pts[1].x, pts[1].y);
                ctx.stroke();
            }
            return;
        }
        const [a, b, c] = [pts[n - 3], pts[n - 2], pts[n - 1]];
        const m1 = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const m2 = { x: (b.x + c.x) / 2, y: (b.y + c.y) / 2 };
        strokeStyle(ctx, s);
        ctx.lineWidth = widthFor(s, b.p);
        ctx.beginPath();
        ctx.moveTo(m1.x, m1.y);
        ctx.quadraticCurveTo(b.x, b.y, m2.x, m2.y);
        ctx.stroke();
    }, []);

    const redrawAll = useCallback(() => {
        const ctx = getInkCtx();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        for (const s of strokesRef.current) {
            for (let i = 2; i <= s.points.length; i++) {
                drawSegment(ctx, s, s.points.slice(0, i));
            }
        }
    }, [drawSegment]);

    // 포인터 핸들러 (리스너는 필기 단계에서 1회 등록)
    useEffect(() => {
        if (step !== 'draw') return;
        const ink = inkRef.current;

        const accepts = (e) => e.pointerType === 'pen' || (toolRef.current.allowTouch && e.pointerType === 'touch') || e.pointerType === 'mouse';

        const onDown = (e) => {
            if (!accepts(e)) return;
            e.preventDefault();
            ink.setPointerCapture(e.pointerId);
            const { tool, color, size } = toolRef.current;
            currentRef.current = { tool, color, size, points: [toCanvasPoint(e)] };
        };
        const onMove = (e) => {
            const cur = currentRef.current;
            if (!cur || !accepts(e)) return;
            e.preventDefault();
            const ctx = getInkCtx();
            const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
            for (const ev of events) {
                cur.points.push(toCanvasPoint(ev));
                drawSegment(ctx, cur, cur.points);
            }
        };
        const onUp = (e) => {
            const cur = currentRef.current;
            if (!cur) return;
            currentRef.current = null;
            if (cur.points.length > 1) {
                strokesRef.current.push(cur);
                redoRef.current = [];
                setCanUndo(true); setCanRedo(false);
            }
        };

        ink.addEventListener('pointerdown', onDown);
        ink.addEventListener('pointermove', onMove, { passive: false });
        ink.addEventListener('pointerup', onUp);
        ink.addEventListener('pointercancel', onUp);
        return () => {
            ink.removeEventListener('pointerdown', onDown);
            ink.removeEventListener('pointermove', onMove);
            ink.removeEventListener('pointerup', onUp);
            ink.removeEventListener('pointercancel', onUp);
        };
    }, [step, toCanvasPoint, drawSegment]);

    const undo = () => {
        const s = strokesRef.current.pop();
        if (s) { redoRef.current.push(s); redrawAll(); }
        setCanUndo(strokesRef.current.length > 0);
        setCanRedo(redoRef.current.length > 0);
    };
    const redo = () => {
        const s = redoRef.current.pop();
        if (s) { strokesRef.current.push(s); redrawAll(); }
        setCanUndo(strokesRef.current.length > 0);
        setCanRedo(redoRef.current.length > 0);
    };
    const clearAll = () => {
        if (strokesRef.current.length === 0) return;
        if (!window.confirm('필기 내용을 모두 지울까요?')) return;
        strokesRef.current = [];
        redoRef.current = [];
        redrawAll();
        setCanUndo(false); setCanRedo(false);
    };

    // ── 화면 고정 모드 (전체화면 + Wake Lock) ──
    // 화면 전환·이동 전 전체화면 해제 (2026-08-21 대표 리포트: 고정 상태에서 [돌아가기] 시
    // 전체화면 대상 div가 언마운트돼 브라우저가 사라진 요소를 붙든 채 흰 화면이 되던 버그)
    const releasePin = async () => {
        try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* 이미 해제됨 */ }
        try { wakeLockRef.current?.release?.(); } catch { /* 무시 */ }
        wakeLockRef.current = null;
        setPinned(false);
    };
    const togglePin = async () => {
        try {
            if (!pinned) {
                await pageRef.current?.requestFullscreen?.();
                if ('wakeLock' in navigator) {
                    wakeLockRef.current = await navigator.wakeLock.request('screen');
                }
                setPinned(true);
            } else {
                if (document.fullscreenElement) await document.exitFullscreen();
                wakeLockRef.current?.release?.();
                wakeLockRef.current = null;
                setPinned(false);
            }
        } catch (e) { console.error('고정 모드 실패:', e); }
    };
    // 안전망: 어떤 경로로든 이 화면을 떠날 때 전체화면이 남아 있으면 해제 (흰 화면 방지)
    useEffect(() => () => {
        try { if (document.fullscreenElement) document.exitFullscreen(); } catch { /* 무시 */ }
    }, []);
    useEffect(() => {
        const onFsChange = () => { if (!document.fullscreenElement) setPinned(false); };
        document.addEventListener('fullscreenchange', onFsChange);
        // 탭 복귀 시 Wake Lock 재요청
        const onVisible = async () => {
            if (pinned && document.visibilityState === 'visible' && 'wakeLock' in navigator) {
                try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('fullscreenchange', onFsChange);
            document.removeEventListener('visibilitychange', onVisible);
            wakeLockRef.current?.release?.();
        };
    }, [pinned]);

    // 이탈 방지 (필기 중)
    useEffect(() => {
        const guard = (e) => {
            if (step === 'draw' && strokesRef.current.length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', guard);
        return () => window.removeEventListener('beforeunload', guard);
    }, [step]);

    // ── 제출 ──
    const handleSubmit = async () => {
        if (strokesRef.current.length === 0) { alert('필기 내용이 없습니다.'); return; }
        if (submitting) return;
        setSubmitting(true);
        try {
            // 배경 + 잉크 합성
            const out = document.createElement('canvas');
            out.width = CANVAS_W; out.height = CANVAS_H;
            const ctx = out.getContext('2d');
            ctx.drawImage(bgRef.current, 0, 0);
            ctx.drawImage(inkRef.current, 0, 0);
            const blob = await new Promise(r => out.toBlob(r, 'image/png'));

            const storageRef = ref(storage, `workOrders/${user.uid}/handwritten_${Date.now()}.png`);
            await uploadBytes(storageRef, blob);
            const imageUrl = await getDownloadURL(storageRef);

            // ── 수정 모드: 새 필기로 교체, 이전 필기는 이력으로 보관 ──
            if (editOrder) {
                await updateDoc(doc(db, 'workOrders', editOrder.id), {
                    handwrittenImageUrl: imageUrl,
                    handwrittenHistory: arrayUnion({
                        url: editOrder.handwrittenImageUrl,
                        replacedAt: new Date(),
                    }),
                    updatedAt: new Date(),
                    isEdited: true,
                    editedAt: new Date(),
                    readByReceiver: false,
                    readAt: null,
                });
                alert('필기 주문이 수정되었습니다!');
                await releasePin();   // 전체화면 상태로 라우팅하면 흰 화면 (2026-08-21)
                navigate(`/view-order/${editOrder.id}`);
                return;
            }

            const fromUserId = targetUserId;
            const fromUserSnap = await getDoc(doc(db, 'users', fromUserId));
            const fromUserName = fromUserSnap.data()?.businessName || fromUserSnap.data()?.name || '';

            const today = new Date();
            const orderNumber = `${today.toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

            const orderRef = await addDoc(collection(db, 'workOrders'), {
                orderNumber,
                orderType: 'handwritten',
                fromUserId, fromUserName,
                toUserId: partner.id, toUserName: partner.name,
                participants: [fromUserId, partner.id],
                isInternalLab: false,
                patientName: '(필기)',
                items: [],
                prosthesisGroups: [], bridgeGroups: [], ponticGroups: [], implantGroups: [],
                specialNotes: '',
                imageUrls: [],                     // 첨부파일로는 넣지 않음 — 의뢰서 본문에만 표시
                handwrittenImageUrl: imageUrl,
                isRemake: false, remakeType: '', remakeReason: '',
                status: 'pending',
                createdAt: new Date(), updatedAt: new Date(),
                digitalFiles: [], designStatus: null,
                scanId: linkedScan?.id || null,
            });

            // 스캔 연결: 파일 전송 요청 (에이전트가 감지해 zip 업로드 → 기공소 자동 수신)
            if (linkedScan?.id) {
                try {
                    const scanRef = doc(db, 'scans', linkedScan.id);
                    // 최신 상태를 다시 읽어 ready일 때만 ordered로 전환 (stale status 되쓰기 방지)
                    const freshSnap = await getDoc(scanRef);
                    const freshStatus = freshSnap.exists() ? freshSnap.data().status : null;
                    await updateDoc(scanRef, {
                        orderId: orderRef.id,
                        labId: partner.id,
                        uploadRequested: true,
                        updatedAt: new Date(),
                        ...(freshStatus === 'ready' ? { status: 'ordered' } : {}),
                    });
                } catch (e) { console.error('스캔 연결 실패:', e); }
            }

            try { await notifyOrderCreated(partner.id, orderRef.id, orderNumber, fromUserName); } catch {}
            try { await getOrCreateChatRoom(fromUserId, partner.id, 'system', orderRef.id); } catch {}

            alert(`필기 주문이 전송되었습니다!\n주문번호: ${orderNumber}`);
            setLinkedScan(null); // 스캔은 1건에만 연결
            // 필기 전용 태블릿 운영: 제출 후 같은 화면에서 새 필기 시작
            strokesRef.current = [];
            redoRef.current = [];
            redrawAll();
            setCanUndo(false); setCanRedo(false);
        } catch (e) {
            console.error('필기 주문 전송 실패:', e);
            alert('전송에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    // ══════════ 렌더 ══════════
    if (step === 'partner') {
        return (
            <div style={styles.partnerPage}>
                <div style={styles.partnerBox}>
                    <PenLine size={30} color="#6366f1" />
                    <div style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', marginTop: 10 }}>필기 주문</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 6, marginBottom: 18 }}>
                        의뢰서를 보낼 기공소를 선택하면 필기 화면이 열립니다
                    </div>
                    {scanIntakeOn && linkedScan && (
                        <div style={styles.scanBanner}>
                            <ScanLine size={15} color="#6366f1" />
                            <span>
                                스캔 연결됨 — {linkedScan.patientNameGuess || linkedScan.caseLabel}
                                {' '}(파일 {(linkedScan.files || []).length}개, 제출 시 자동 전송)
                            </span>
                        </div>
                    )}
                    {partners.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94a3b8', padding: 20 }}>연결된 거래처가 없습니다</div>
                    ) : partners.map(p => (
                        <button key={p.id} style={styles.partnerItem} onClick={() => { setPartner(p); setStep('draw'); }}>
                            <Handshake size={16} color="#6366f1" />
                            <span style={{ flex: 1, textAlign: 'left' }}>{p.name}</span>
                            {p.templateUrl && <span style={styles.customBadge}>전용 양식</span>}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div ref={pageRef} style={styles.drawPage}>
            {/* 상단 툴바 */}
            <div style={styles.toolbar}>
                <button style={styles.toolBtn} onClick={async () => {
                    if (strokesRef.current.length > 0 && !window.confirm('필기 내용이 사라집니다. 나갈까요?')) return;
                    await releasePin();   // 전체화면 해제 후 이동 — 흰 화면 방지 (2026-08-21)
                    if (editOrder) { navigate(`/view-order/${editOrder.id}`); return; }
                    setStep('partner');
                }}><ChevronLeft size={18} /></button>

                <span style={styles.partnerLabel}>{partner?.name}{editOrder ? ' · 수정 중' : ''}</span>
                {scanIntakeOn && linkedScan && (
                    <span style={{ ...styles.partnerLabel, color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ScanLine size={13} />스캔 연결
                    </span>
                )}

                <div style={styles.toolGroup}>
                    <button style={{ ...styles.toolBtn, ...(tool === 'pen' ? styles.toolActive : {}) }} onClick={() => setTool('pen')} title="펜"><PenLine size={17} /></button>
                    <button style={{ ...styles.toolBtn, ...(tool === 'highlighter' ? styles.toolActive : {}) }} onClick={() => setTool('highlighter')} title="형광펜"><Highlighter size={17} /></button>
                    <button style={{ ...styles.toolBtn, ...(tool === 'eraser' ? styles.toolActive : {}) }} onClick={() => setTool('eraser')} title="지우개"><Eraser size={17} /></button>
                </div>

                <div style={styles.toolGroup}>
                    {COLORS.map(c => (
                        <button key={c} onClick={() => { setColor(c); if (tool === 'eraser') setTool('pen'); }}
                            style={{ ...styles.colorDot, backgroundColor: c, outline: color === c ? '3px solid #a5b4fc' : 'none' }} />
                    ))}
                    <input type="color" value={color} onChange={e => setColor(e.target.value)} style={styles.colorPicker} title="색상 선택" />
                </div>

                <div style={styles.toolGroup}>
                    {SIZES.map(s => (
                        <button key={s} onClick={() => setSize(s)}
                            style={{ ...styles.sizeBtn, outline: size === s ? '2px solid #6366f1' : '1px solid #e2e8f0' }}>
                            <span style={{ width: Math.min(s * 1.6, 18), height: Math.min(s * 1.6, 18), borderRadius: '50%', backgroundColor: '#334155', display: 'inline-block' }} />
                        </button>
                    ))}
                </div>

                <div style={styles.toolGroup}>
                    <button style={{ ...styles.toolBtn, opacity: canUndo ? 1 : 0.3 }} onClick={undo} disabled={!canUndo} title="실행취소"><Undo2 size={17} /></button>
                    <button style={{ ...styles.toolBtn, opacity: canRedo ? 1 : 0.3 }} onClick={redo} disabled={!canRedo} title="다시실행"><Redo2 size={17} /></button>
                    <button style={styles.toolBtn} onClick={clearAll} title="전체 지우기"><Trash2 size={17} /></button>
                </div>

                <div style={{ flex: 1 }} />

                <label style={styles.touchToggle}>
                    <input type="checkbox" checked={allowTouch} onChange={e => setAllowTouch(e.target.checked)} />
                    손가락 필기
                </label>
                <button style={{ ...styles.toolBtn, ...(pinned ? styles.toolActive : {}) }} onClick={togglePin} title="화면 고정 (전체화면 + 꺼짐 방지)">
                    {pinned ? <Minimize size={17} /> : <Maximize size={17} />}
                </button>
                <button style={styles.sendBtn} onClick={handleSubmit} disabled={submitting}>
                    {submitting ? <Loader size={16} style={{ animation: 'hwspin 1s linear infinite' }} /> : <Send size={16} />}
                    {submitting ? '전송 중...' : (editOrder ? '수정 저장' : '보내기')}
                </button>
            </div>

            {/* 캔버스 */}
            <div ref={wrapRef} style={styles.canvasWrap}>
                <div style={styles.canvasStack}>
                    <canvas ref={bgRef} style={styles.canvasLayer} />
                    <canvas ref={inkRef} style={{ ...styles.canvasLayer, touchAction: 'none', position: 'absolute', top: 0, left: 0 }} />
                </div>
            </div>
            <style>{'@keyframes hwspin { to { transform: rotate(360deg); } } body { overscroll-behavior: none; }'}</style>
        </div>
    );
}

const styles = {
    partnerPage: { display: 'flex', justifyContent: 'center', padding: '40px 16px' },
    partnerBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '30px 26px', width: 'min(440px, 100%)',
    },
    partnerItem: {
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '13px 16px', marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#0f172a',
        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer',
    },
    customBadge: { fontSize: 11, fontWeight: 700, color: '#6366f1', backgroundColor: '#eef2ff', padding: '2px 8px', borderRadius: 6 },
    scanBanner: {
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8,
        padding: '10px 12px', marginBottom: 14, fontSize: 12.5, fontWeight: 600, color: '#3730a3',
    },

    drawPage: { display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#334155' },
    toolbar: {
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '8px 12px', backgroundColor: '#1e293b',
    },
    partnerLabel: { color: '#e2e8f0', fontSize: 13, fontWeight: 700, marginRight: 4 },
    toolGroup: { display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px', borderLeft: '1px solid #334155' },
    toolBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, borderRadius: 8, border: 'none',
        backgroundColor: '#334155', color: '#cbd5e1', cursor: 'pointer',
    },
    toolActive: { backgroundColor: '#6366f1', color: '#fff' },
    colorDot: { width: 26, height: 26, borderRadius: '50%', border: '2px solid #1e293b', cursor: 'pointer' },
    colorPicker: { width: 30, height: 30, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', padding: 0 },
    sizeBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, borderRadius: 8, border: 'none', backgroundColor: '#f8fafc', cursor: 'pointer',
    },
    touchToggle: { display: 'flex', alignItems: 'center', gap: 5, color: '#94a3b8', fontSize: 12, marginRight: 4 },
    sendBtn: {
        display: 'flex', alignItems: 'center', gap: 7,
        backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
        padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    },
    canvasWrap: {
        flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center',
        padding: 12, overscrollBehavior: 'none',
    },
    canvasStack: {
        position: 'relative', backgroundColor: '#fff',
        width: 'min(100%, calc((100vh - 90px) * 0.707))',
        aspectRatio: '1240 / 1754',
        boxShadow: '0 8px 30px rgba(0,0,0,0.35)', borderRadius: 4,
    },
    canvasLayer: { width: '100%', height: '100%', display: 'block', borderRadius: 4 },
};

export default HandwrittenOrder;
